import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { adminApi } from '@/api/admin'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { categoryErrorMessage } from './categoryErrors'

const NEW = '__new__'

/** Pick a course's category, or create one without leaving the form. */
export default function CourseCategorySelect({
  id, categoryId, legacyName, onChange,
}: {
  id?: string
  categoryId: string | null
  /** The course's old free-text category, shown until it is moved into a real one. */
  legacyName?: string | null
  onChange: (categoryId: string | null, name: string | null) => void
}) {
  const queryClient = useQueryClient()
  const { data: categories = [] } = useQuery({
    queryKey: ['course-categories'],
    queryFn: () => adminApi.getCourseCategories().then(r => r.data),
  })

  const [adding, setAdding] = useState(false)
  const [newName, setNewName] = useState('')
  const [error, setError] = useState<string | null>(null)

  const createMutation = useMutation({
    mutationFn: (name: string) => adminApi.createCourseCategory({ name }).then(r => r.data.id),
    onSuccess: async (newId, name) => {
      await queryClient.invalidateQueries({ queryKey: ['course-categories'] })
      onChange(newId, name.trim())
      setAdding(false)
      setNewName('')
      setError(null)
    },
    onError: err => setError(categoryErrorMessage(err, 'Could not add the category.')),
  })

  const submitNew = () => {
    const name = newName.trim()
    if (!name) { setError('Enter a name for the category.'); return }
    const existing = categories.find(c => c.name.toLowerCase() === name.toLowerCase())
    if (existing) {
      onChange(existing.id, existing.name)
      setAdding(false)
      setNewName('')
      setError(null)
      return
    }
    createMutation.mutate(name)
  }

  // A course whose category has not been moved across yet (or a category deleted elsewhere) still shows something sensible.
  const selectedKnown = categoryId == null || categories.some(c => c.id === categoryId)

  return (
    <div className="space-y-2">
      <select
        id={id}
        value={adding ? NEW : (categoryId ?? '')}
        onChange={e => {
          const v = e.target.value
          if (v === NEW) { setAdding(true); setError(null); return }
          setAdding(false)
          const chosen = categories.find(c => c.id === v)
          onChange(chosen?.id ?? null, chosen?.name ?? null)
        }}
        className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
      >
        <option value="">— No category —</option>
        {!selectedKnown && <option value={categoryId ?? ''}>{legacyName ?? 'Loading…'}</option>}
        {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        <option value={NEW}>+ New category…</option>
      </select>

      {adding && (
        <div className="space-y-1.5">
          <div className="flex gap-2">
            <Input
              autoFocus
              value={newName}
              maxLength={100}
              onChange={e => { setNewName(e.target.value); setError(null) }}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submitNew() } if (e.key === 'Escape') setAdding(false) }}
              placeholder="e.g. Health & Safety"
            />
            <Button type="button" size="sm" onClick={submitNew} disabled={createMutation.isPending}>
              {createMutation.isPending ? 'Adding…' : 'Add'}
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={() => { setAdding(false); setError(null) }}>Cancel</Button>
          </div>
          {error && <p className="text-xs text-red-600">{error}</p>}
        </div>
      )}
    </div>
  )
}
